import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Grid,
  RadioGroup,
  Radio,
  Divider,
  Tooltip,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import { baseUrl } from '../api';
import { AuthContext } from '../context/AuthContext';
import { useAxiosAuth } from '../hooks/useAxiosAuth';
import { useTheme, alpha } from '@mui/material/styles';

const QUESTION_TYPES = [
  { value: 'mcq_single', label: 'Multiple Choice (Single Select)' },
  { value: 'mcq_multi', label: 'Multiple Choice (Multi Select)' },
  { value: 'short_word', label: 'Short Word' },
  { value: 'numeric', label: 'Numerical Answer' },
  { value: 'true_false', label: 'True or False' },
  { value: 'essay', label: 'Essay Upload' },
];

const TEMPLATES = [
  { value: 'custom',       label: 'Custom' },
  { value: 'stem_weekly',  label: 'STEM – Weekly Quiz' },
  { value: 'stem_midterm', label: 'STEM – Midterm' },
  { value: 'stem_final',   label: 'STEM – Final' },
  { value: 'jee-math',     label: 'College Admissions Prep - MATH' },
  { value: 'jee-phys',     label: 'College Admissions Prep - PHYS' },
  { value: 'jee-chem',     label: 'College Admissions Prep - CHEM' },
  { value: 'act-eng',      label: 'ACT - ENG' },
  { value: 'act-math',     label: 'ACT - MATH' },
  { value: 'act-science',  label: 'ACT - SCIENCE' },
  { value: 'act-rd',       label: 'ACT - READING' },
  { value: 'act-wr',       label: 'ACT - WRITING' },

  { value: 'sat-rw',       label: 'SAT-RW' },
  { value: 'sat-math',     label: 'SAT-MATH' },
];

export default function ExamGenerator() {
  const { user } = useContext(AuthContext);

  // fetched data
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);

  // selections
  const [selectedCourse, setSelectedCourse] = useState('');
  const [lessonIds, setLessonIds] = useState([]);

  // scope: 'course' | 'lessons'
  const [scope, setScope] = useState('lessons');

  // creation
  const creationSteps = ['Draft Quiz', 'Review & Edit'];
  const [creationStep, setCreationStep] = useState(0);

  // step0 inputs
  const [quizName, setQuizName] = useState('');

  // per-type counts
  const [questionMix, setQuestionMix] = useState({
    mcq_single: 5, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0,
  });

  // content format
  const [contentFormat, setContentFormat] = useState('mindengage_html'); // or 'plain_text'

  // layout template
  const [layoutTemplate, setLayoutTemplate] = useState('custom');

  // ---------- NEW: Difficulty variant (easy | medium | hard) ----------
  const [difficultyVariant, setDifficultyVariant] = useState('medium');

  // drafts
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);

  // raw spec JSON & parsed (LMS-native schema)
  const [specJson, setSpecJson] = useState('');
  const [specObj, setSpecObj] = useState({ questions: [] });

  // loading
  const [loading, setLoading] = useState(false);

  const axiosInstance = useAxiosAuth();

  const totalRequested = Object.values(questionMix).reduce((a, b) => a + (Number(b) || 0), 0);

  const isLikelyHTML = (s) => typeof s === 'string' && /<\s*([a-z]+)(\s|>)/i.test(s);

  const HTMLPreview = ({ html }) => (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, overflowX: 'auto', bgcolor: 'background.default' }}>
      {/* sanitize if needed */}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );

  // ---------- NEW: Topic Text state ----------
  const [topicText, setTopicText] = useState('');
  const MAX_TOPIC_CHARS = 8000;

  // ---------- NEW: Section coloring helpers ----------
  const [useSectionColoring, setUseSectionColoring] = useState(true);
  const diffColor = (d) => (d === 'easy' ? 'success' : d === 'hard' ? 'error' : 'warning');
  const useSectionColors = () => {
    const theme = useTheme();
    const mk = (color) => ({
      borderLeft: 6,
      borderLeftColor: color,
      bgcolor: alpha(color, 0.08),
    });
    return {
      scope:     mk(theme.palette.info.main),
      template:  mk(theme.palette.secondary.main),
      format:    mk(theme.palette.grey[600]),
      difficulty:mk(theme.palette.warning.main),
      mix:       mk(theme.palette.success.main),
      meta:      mk(theme.palette.primary.main),
      editor:    mk(theme.palette.primary.main),
    };
  };

  // ---------- Fetch base data ----------
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get('/courses/');
        setCourses(resp.data);
      } catch (err) {
        console.error('Failed to load courses', err);
      }
    })();
  }, []);

  // when course changes: reset, fetch lessons + drafts
  useEffect(() => {
    setLessonIds([]);
    setLessons([]);
    setDrafts([]);
    setCreationStep(0);
    setDraftId(null);
    setSpecJson('');
    setSpecObj({ questions: [] });
    setQuizName('');

    if (!selectedCourse) return;

    axiosInstance
      .get(`/courses/${selectedCourse}`)
      .then(resp => setLessons(resp.data.lessons || []))
      .catch(err => console.error('Failed to load lessons', err));

    fetchDrafts();
  }, [selectedCourse]);

  const fetchDrafts = async () => {
    try {
      const resp = await axiosInstance.get(`/exams/quizzes/${selectedCourse}`);
      setDrafts(resp.data.filter(d => d.course_id === selectedCourse));
    } catch (err) {
      console.error('Failed to load drafts', err);
    }
  };

  // parse raw specJson into specObj
  useEffect(() => {
    try {
      if (!specJson) { setSpecObj({ questions: [] }); return; }
      const obj = JSON.parse(specJson);
      setSpecObj(obj);
    } catch (e) {
      console.error('Failed to parse specJson', e);
    }
  }, [specJson]);

  // ---------- Template presets ----------
  const applyTemplate = (tpl) => {
    setLayoutTemplate(tpl);
    switch (tpl) {
      // ——— JEE (Main) Paper 1: per subject ———
      // Section A: 20 MCQ (single-correct), Section B: 5 Numerical (no optional as of 2025)
      case 'jee-math':
      case 'jee-phys':
      case 'jee-chem':
        setQuestionMix({ mcq_single: 20, mcq_multi: 0, short_word: 0, numeric: 5, true_false: 0, essay: 0 });
        break;
  
      // ——— ACT (Enhanced ACT, 2025) ———
      // English: 50 MCQ (40 scored), Math: 45 MCQ (41 scored),
      // Reading: 36 MCQ (27 scored), Science: 40 MCQ (34 scored, now optional),
      // Writing: 1 essay (optional)
      case 'act-eng':
        setQuestionMix({ mcq_single: 50, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0 });
        break;
      case 'act-math':
        setQuestionMix({ mcq_single: 45, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0 });
        break;
      case 'act-rd':
        setQuestionMix({ mcq_single: 36, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0 });
        break;
      case 'act-science': // optional section
        setQuestionMix({ mcq_single: 40, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0 });
        break;
      case 'act-wr': // optional Writing = 1 essay prompt
        setQuestionMix({ mcq_single: 0, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 1 });
        break;
  
      // ——— Digital SAT (2024+) ———
      // R&W: 54 MCQ total (27 + 27)
      // Math: 44 total (33 MCQ + 11 student-produced/numeric)
      case 'sat-rw':
        setQuestionMix({ mcq_single: 54, mcq_multi: 0, short_word: 0, numeric: 0, true_false: 0, essay: 0 });
        break;
      case 'sat-math':
        setQuestionMix({ mcq_single: 33, mcq_multi: 0, short_word: 0, numeric: 11, true_false: 0, essay: 0 });
        break;
  
      // ——— Custom STEM presets you already had ———
      case 'stem_weekly':
        setQuestionMix({ mcq_single: 4, mcq_multi: 2, short_word: 1, numeric: 2, true_false: 1, essay: 0 });
        break;
      case 'stem_midterm':
        setQuestionMix({ mcq_single: 10, mcq_multi: 2, short_word: 4, numeric: 4, true_false: 0, essay: 1 });
        break;
      case 'stem_final':
        setQuestionMix({ mcq_single: 14, mcq_multi: 3, short_word: 4, numeric: 6, true_false: 0, essay: 1 });
        break;
  
      default:
        setQuestionMix(q => ({ ...q }));
    }
  };
  

  // ---------- Spec editing helpers (NEW schema) ----------
  const ensureArray = (v) => (Array.isArray(v) ? v : []);
  const getChoices = (q) => ensureArray(q.choices);

  const setQuestion = (qIdx, updater) => {
    setSpecObj(prev => {
      const qs = [...prev.questions];
      const old = qs[qIdx] || {};
      qs[qIdx] = updater({ ...old });
      return { ...prev, questions: qs };
    });
  };

  const updateQuestionField = (qIdx, field, value) => {
    setQuestion(qIdx, q => ({ ...q, [field]: value }));
  };

  const addChoice = (qIdx) => {
    setQuestion(qIdx, q => {
      const choices = getChoices(q);
      // default next id: A/B/C/D/E...
      const nextId = String.fromCharCode(65 + choices.length);
      return { ...q, choices: [...choices, { id: nextId, label_html: '' }] };
    });
  };

  const removeChoice = (qIdx, choiceIdx) => {
    setQuestion(qIdx, q => {
      const choices = getChoices(q);
      const newChoices = choices.filter((_, i) => i !== choiceIdx);
      // also clean answer_key to remove any now-missing ids
      const ids = new Set(newChoices.map(c => c.id));
      const ak = ensureArray(q.answer_key).filter(id => ids.has(id));
      return { ...q, choices: newChoices, answer_key: ak };
    });
  };

  const updateChoiceField = (qIdx, choiceIdx, field, value) => {
    setQuestion(qIdx, q => {
      const choices = getChoices(q);
      const old = choices[choiceIdx] || { id: '', label_html: '' };
      const updated = { ...old, [field]: value };
      const newChoices = choices.map((c, i) => (i === choiceIdx ? updated : c));
      // If ID changed, sync answer_key
      let ak = ensureArray(q.answer_key);
      if (field === 'id') {
        ak = ak.map(id => (id === old.id ? value : id));
      }
      return { ...q, choices: newChoices, answer_key: ak };
    });
  };

  const toggleAnswerKeyId = (qIdx, id, multi) => {
    setQuestion(qIdx, q => {
      const ak = new Set(ensureArray(q.answer_key));
      if (multi) {
        ak.has(id) ? ak.delete(id) : ak.add(id);
        return { ...q, answer_key: Array.from(ak) };
      }
      // single
      return { ...q, answer_key: [id] };
    });
  };

  const ensureTrueFalseChoices = (qIdx) => {
    setQuestion(qIdx, q => {
      let choices = getChoices(q);
      const ids = new Set(choices.map(c => c.id));
      if (!ids.has('true')) choices = [{ id: 'true', label_html: '<p>True</p>' }, ...choices];
      if (!ids.has('false')) choices = [...choices, { id: 'false', label_html: '<p>False</p>' }];
      // normalize to exactly two
      choices = choices.filter(c => c.id === 'true' || c.id === 'false');
      // default answer_key if missing
      const ak = ensureArray(q.answer_key);
      return { ...q, choices, answer_key: ak.length ? ak : ['true'] };
    });
  };

  const addQuestion = () => {
    setSpecObj(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          type: 'mcq_single',
          prompt_html: '',
          choices: [
            { id: 'A', label_html: '' },
            { id: 'B', label_html: '' },
            { id: 'C', label_html: '' },
            { id: 'D', label_html: '' },
          ],
          answer_key: ['A'],
          points: 1,
        },
      ],
    }));
  };

  const removeQuestion = idx => {
    setSpecObj(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  // ---------- Step 0 → generate new draft ----------
  const handleNext = async () => {
    setLoading(true);
    try {
      const mixArray = Object.entries(questionMix)
        .filter(([, count]) => Number(count) > 0)
        .map(([type, count]) => ({ type, count: Number(count) }));

      const payload = {
        course_id: scope === 'topic_text' ? null : selectedCourse,       // NEW
        scope,
        lesson_ids: scope === 'lessons' ? lessonIds : [],               // unchanged for lessons
        quiz_name: quizName,
        topic_text: scope === 'topic_text' ? topicText : null,          // NEW
        question_mix: mixArray,
        content_format: contentFormat,
        layout_template: layoutTemplate,
        difficulty_variant: difficultyVariant,                          // NEW (optional; backend default = "medium")
      };

      const resp = await axiosInstance.post('/exams/generate_quiz', payload);
      setDraftId(resp.data.draft_id);
      setSpecJson(JSON.stringify(resp.data.spec, null, 2));
      setCreationStep(1);
    } catch (err) {
      console.error(err);
      alert('Failed to generate draft');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Step 1 → save edits ----------
  const handleSave = async () => {
    setLoading(true);
    try {
      const newJson = JSON.stringify(specObj, null, 2);
      await axiosInstance.patch(`/exams/quiz/${draftId}`, {
        name: quizName,
        spec_json: newJson,
      });
      setSpecJson(newJson);
      alert('Draft updated!');
      fetchDrafts();
    } catch (err) {
      console.error(err);
      alert('Failed to save quiz spec');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Draft deletion ----------
  const handleDelete = async id => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axiosInstance.delete(`/exams/quiz/${id}`);
      setDrafts(drafts.filter(d => d.id !== id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete draft');
    }
  };

  // ---------- Load a draft into editor ----------
  const handleEdit = async (draft) => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get(`/exams/quiz/${draft.id}`);
      const { spec } = resp.data;
      if (typeof spec !== 'object' || spec === null || Array.isArray(spec)) {
        throw new Error('Invalid spec: expected a JSON object');
      }
      if (!spec.questions || !Array.isArray(spec.questions)) {
        throw new Error('Invalid spec: "questions" field is missing or malformed');
      }
      setDraftId(resp.data.draft_id);
      setQuizName(resp.data.name);
      setSpecJson(JSON.stringify(resp.data.spec, null, 2));
      setCreationStep(1);
    } catch (err) {
      console.error('Failed to load draft', err);
      alert('Unable to load draft for editing.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- UI ----------
  const section = useSectionColors();

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5">Generate Exam</Typography>
          <Tooltip title="Use templates to quickly prefill counts">
            <AutoAwesomeIcon fontSize="small" />
          </Tooltip>
          <FormControlLabel
            sx={{ ml: 'auto' }}
            control={
              <Checkbox
                checked={useSectionColoring}
                onChange={(e) => setUseSectionColoring(e.target.checked)}
              />
            }
            label="Section colors"
          />
        </Box>

        {/* Course selector */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="course-label">Course</InputLabel>
          <Select
            labelId="course-label"
            value={selectedCourse}
            label="Course"
            onChange={e => setSelectedCourse(e.target.value)}
            disabled={scope === 'topic_text'}                 
          >
            <MenuItem value="">-- Select a Course --</MenuItem>
            {courses.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.subject}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Stepper */}
        <Stepper activeStep={creationStep} sx={{ mb: 3 }}>
          {creationSteps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {creationStep === 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Create New Draft
            </Typography>

            {/* Scope */}
            <Card variant="outlined" sx={{ mb: 2, ...(useSectionColoring ? section.scope : {}) }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Scope</Typography>
                <RadioGroup row value={scope} onChange={(e) => setScope(e.target.value)}>
                  <FormControlLabel value="course" control={<Radio />} label="Whole Course" />
                  <FormControlLabel value="lessons" control={<Radio />} label="Selected Lessons" />
                  <FormControlLabel value="topic_text" control={<Radio />} label="Topic Text" /> {/* NEW */}
                </RadioGroup>

                <FormControl fullWidth sx={{ mt: 2 }} disabled={scope !== 'lessons'}>
                  <InputLabel id="lesson-label">Lessons</InputLabel>
                  <Select
                    labelId="lesson-label"
                    multiple
                    value={lessonIds}
                    label="Lessons"
                    onChange={e => setLessonIds(e.target.value)}
                    renderValue={sel => sel.map(id => lessons.find(l => l.id === id)?.title || id).join(', ')}
                  >
                    {lessons.map(l => (
                      <MenuItem key={l.id} value={l.id}>
                        <Checkbox checked={lessonIds.includes(l.id)} />
                        <ListItemText primary={l.title} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* NEW: Topic Text editor */}
                {scope === 'topic_text' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Paste a concise list of topics/concepts to assess. This acts as the outline for generation.
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      label="Topic Text"
                      value={topicText}
                      onChange={(e) => setTopicText(e.target.value.slice(0, MAX_TOPIC_CHARS))}
                      helperText={`${topicText.length}/${MAX_TOPIC_CHARS} characters`}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Template + Content Format */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ ...(useSectionColoring ? section.template : {}) }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Exam Layout Template</Typography>
                    <FormControl fullWidth>
                      <InputLabel id="tpl-label">Template</InputLabel>
                      <Select
                        labelId="tpl-label"
                        value={layoutTemplate}
                        label="Template"
                        onChange={(e) => applyTemplate(e.target.value)}
                      >
                        {TEMPLATES.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Choosing a template prefills question-type counts. You can still tweak them below.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ ...(useSectionColoring ? section.format : {}) }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Question Content Format</Typography>
                    <RadioGroup value={contentFormat} onChange={(e) => setContentFormat(e.target.value)}>
                      <FormControlLabel value="mindengage_html" control={<Radio />} label="HTML/SVG (MindEngage export)" />
                      <FormControlLabel value="plain_text" control={<Radio />} label="Plain text (Google Forms export)" />
                    </RadioGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* NEW: Difficulty Variant */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ ...(useSectionColoring ? section.difficulty : {}) }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>Difficulty Variant</Typography>
                      <Chip
                        size="small"
                        label={difficultyVariant.toUpperCase()}
                        color={diffColor(difficultyVariant)}
                        variant="filled"
                      />
                    </Box>
                    <RadioGroup
                      row
                      value={difficultyVariant}
                      onChange={(e) => setDifficultyVariant(e.target.value)}
                    >
                      <FormControlLabel value="easy" control={<Radio />} label="Easy" />
                      <FormControlLabel value="medium" control={<Radio />} label="Balanced" />
                      <FormControlLabel value="hard" control={<Radio />} label="Hard" />
                    </RadioGroup>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This influences how items are written (e.g., inference depth, distractor rigor). Counts still come from the template & fields above.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Question Type Mix */}
            <Card variant="outlined" sx={{ mb: 2, ...(useSectionColoring ? section.mix : {}) }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Question Types & Counts</Typography>
                  <Typography variant="body2" color="text.secondary">Total: {totalRequested}</Typography>
                </Box>
                <Grid container spacing={2}>
                  {QUESTION_TYPES.map((qt) => (
                    <Grid key={qt.value} item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        type="number"
                        fullWidth
                        label={qt.label}
                        value={questionMix[qt.value] ?? 0}
                        inputProps={{ min: 0 }}
                        onChange={(e) => setQuestionMix(prev => ({ ...prev, [qt.value]: Number(e.target.value) }))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Quiz meta */}
            <Card variant="outlined" sx={{ mb: 2, ...(useSectionColoring ? section.meta : {}) }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Quiz Name"
                      value={quizName}
                      onChange={e => setQuizName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="(Optional) Notes" placeholder="e.g., Chapter 1–3 focus" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  loading || !quizName ||
                  (scope !== 'topic_text' && !selectedCourse) ||
                  (scope === 'lessons' && lessonIds.length === 0) ||
                  (scope === 'topic_text' && !topicText.trim()) ||
                  totalRequested <= 0
                }
                startIcon={loading && <CircularProgress size={20} />}
              >
                Generate Draft
              </Button>
            </Box>

            {/* existing drafts */}
            <Typography variant="h6" gutterBottom>Existing Drafts</Typography>
            {drafts.length === 0 ? (
              <Typography color="text.secondary">No drafts yet.</Typography>
            ) : (
              <Stack spacing={2}>
                {drafts.map(d => (
                  <Card key={d.id} variant="outlined">
                    <CardContent>
                      <Typography>{d.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scope: {d.lesson_ids && d.lesson_ids.length > 0 ? `${d.lesson_ids.length} lessons` : 'Whole course'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(d)}>Edit</Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(d.id)}>Delete</Button>
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            )}
          </>
        )}

        {creationStep === 1 && (
          <>
            <Typography variant="subtitle1" gutterBottom>Review &amp; Edit Draft</Typography>

            {/* allow renaming */}
            <TextField
              fullWidth
              label="Quiz Name"
              value={quizName}
              onChange={e => setQuizName(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* add-question */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addQuestion}>Add Question</Button>
              <Button onClick={() => setCreationStep(0)}>Back</Button>
            </Box>

            {/* question cards */}
            {specObj.questions.map((q, qi) => {
              const choices = getChoices(q);
              const ak = Array.isArray(q.answer_key) ? q.answer_key : [];
              const thisDiff = (q.tags?.difficulty || difficultyVariant);

              return (
                <Card key={qi} sx={{ mb: 2, p: 2, position: 'relative', ...(useSectionColoring ? section.editor : {}) }}>
                  <IconButton aria-label="remove question" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={() => removeQuestion(qi)}>
                    <DeleteIcon />
                  </IconButton>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Question {qi + 1}</Typography>
                    <Chip
                      size="small"
                      label={(thisDiff || 'medium').toUpperCase()}
                      color={diffColor(thisDiff)}
                      variant="outlined"
                    />
                  </Box>

                  {/* Optional ID + points */}
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Question ID (optional)"
                        value={q.id || ''}
                        onChange={(e) => updateQuestionField(qi, 'id', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Points"
                        inputProps={{ min: 0, step: 0.5 }}
                        value={typeof q.points === 'number' ? q.points : 1}
                        onChange={(e) => updateQuestionField(qi, 'points', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel id={`type-${qi}`}>Type</InputLabel>
                        <Select
                          labelId={`type-${qi}`}
                          label="Type"
                          value={q.type || 'mcq_single'}
                          onChange={(e) => {
                            const nextType = e.target.value;
                            updateQuestionField(qi, 'type', nextType);
                            if (nextType === 'true_false') ensureTrueFalseChoices(qi);
                          }}
                        >
                          {QUESTION_TYPES.map(t => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* prompt_html */}
                  {isLikelyHTML(q.prompt_html) && (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }} color="text.secondary">Rendered preview</Typography>
                      <HTMLPreview html={q.prompt_html} />
                    </>
                  )}
                  <TextField
                    multiline
                    fullWidth
                    label={isLikelyHTML(q.prompt_html) ? 'Question (raw HTML)' : 'Question (HTML fragment)'}
                    value={q.prompt_html || ''}
                    onChange={e => updateQuestionField(qi, 'prompt_html', e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  {/* MCQ & True/False choices */}
                  {(q.type === 'mcq_single' || q.type === 'mcq_multi' || q.type === 'true_false') && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Choices</Typography>

                      {choices.map((c, ci) => (
                        <Box key={ci} sx={{ mb: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={2}>
                              <TextField
                                label="ID"
                                value={c.id}
                                onChange={(e) => updateChoiceField(qi, ci, 'id', e.target.value)}
                                disabled={q.type === 'true_false'} // must remain true/false
                              />
                            </Grid>
                            <Grid item xs={12} md={8}>
                              {isLikelyHTML(c.label_html) && (
                                <Typography variant="caption" color="text.secondary">Option preview</Typography>
                              )}
                              {isLikelyHTML(c.label_html) && <HTMLPreview html={c.label_html} />}
                              <TextField
                                fullWidth
                                label={isLikelyHTML(c.label_html) ? 'Label (raw HTML)' : 'Label (HTML fragment)'}
                                value={c.label_html}
                                onChange={(e) => updateChoiceField(qi, ci, 'label_html', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Button
                                color="error"
                                onClick={() => removeChoice(qi, ci)}
                                disabled={q.type === 'true_false'} // enforce exactly two
                              >
                                Remove
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}

                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addChoice(qi)} disabled={q.type === 'true_false'}>
                          Add Choice
                        </Button>
                      </Box>

                      {/* Answer selection */}
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Answer</Typography>
                      {q.type === 'mcq_single' && (
                        <RadioGroup
                          value={ak[0] || ''}
                          onChange={(e) => toggleAnswerKeyId(qi, e.target.value, false)}
                        >
                          {choices.map(c => (
                            <FormControlLabel key={c.id} value={c.id} control={<Radio />} label={c.id} />
                          ))}
                        </RadioGroup>
                      )}
                      {q.type === 'mcq_multi' && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {choices.map(c => (
                            <FormControlLabel
                              key={c.id}
                              control={
                                <Checkbox
                                  checked={ak.includes(c.id)}
                                  onChange={() => toggleAnswerKeyId(qi, c.id, true)}
                                />
                              }
                              label={c.id}
                            />
                          ))}
                        </Box>
                      )}
                      {q.type === 'true_false' && (
                        <RadioGroup
                          value={ak[0] || 'true'}
                          onChange={(e) => toggleAnswerKeyId(qi, e.target.value, false)}
                        >
                          <FormControlLabel value="true" control={<Radio />} label="True" />
                          <FormControlLabel value="false" control={<Radio />} label="False" />
                        </RadioGroup>
                      )}
                    </>
                  )}

                  {/* short_word answers */}
                  {q.type === 'short_word' && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Accepted Answers</Typography>
                      {ensureArray(q.answer_key).map((ans, ai) => (
                        <Box key={ai} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            value={ans}
                            onChange={(e) => {
                              setQuestion(qi, old => {
                                const arr = ensureArray(old.answer_key);
                                arr[ai] = e.target.value;
                                return { ...old, answer_key: arr };
                              });
                            }}
                          />
                          <Button color="error" onClick={() => {
                            setQuestion(qi, old => {
                              const arr = ensureArray(old.answer_key).filter((_, i) => i !== ai);
                              return { ...old, answer_key: arr };
                            });
                          }}>Remove</Button>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setQuestion(qi, old => ({ ...old, answer_key: [...ensureArray(old.answer_key), ''] }))}
                      >
                        Add Accepted Answer
                      </Button>
                    </>
                  )}

                  {/* numeric answer */}
                  {q.type === 'numeric' && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Numeric Answer (canonical string)</Typography>
                      <TextField
                        fullWidth
                        value={ensureArray(q.answer_key)[0] || ''}
                        onChange={(e) => setQuestion(qi, old => ({ ...old, answer_key: [e.target.value] }))}
                      />
                    </>
                  )}

                  {/* essay rubric */}
                  {q.type === 'essay' && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Rubric / Instructions"
                        placeholder="Upload instructions, guidelines, max file size, etc."
                        value={q.rubric || ''}
                        onChange={e => updateQuestionField(qi, 'rubric', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                    </>
                  )}

                  {/* Optional tags */}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tags (optional)</Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Domain"
                        value={q.tags?.domain || ''}
                        onChange={(e) => {
                          const next = { ...(q.tags || {}), domain: e.target.value };
                          updateQuestionField(qi, 'tags', next);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Skill"
                        value={q.tags?.skill || ''}
                        onChange={(e) => {
                          const next = { ...(q.tags || {}), skill: e.target.value };
                          updateQuestionField(qi, 'tags', next);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Difficulty (easy/medium/hard)"
                        value={q.tags?.difficulty || ''}
                        onChange={(e) => {
                          const next = { ...(q.tags || {}), difficulty: e.target.value };
                          updateQuestionField(qi, 'tags', next);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={() => setCreationStep(0)}>Back</Button>
              <Button variant="contained" onClick={handleSave} disabled={loading} startIcon={loading && <CircularProgress size={20} />}>
                Save Draft
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}
